import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, MapPin, Calendar, User, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useAuthModal } from "../../context/AuthModalContext";
import Button from "../ui/Button";
import SearchSuggestions from "../SearchSuggestions";
import LocationSuggestions from "../LocationSuggestions";
import DatePickerPopover from "../DatePickerPopover";
import api from "../../lib/api";
import NotificationBell from "../NotificationBell";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { openAuthModal } = useAuthModal();
  const [q, setQ] = useState("");
  const [where, setWhere] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [whereSuggestOpen, setWhereSuggestOpen] = useState(false);
  const [mobileWhereSuggestOpen, setMobileWhereSuggestOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [date, setDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const isHome = location.pathname === "/";
  const [scrolledPastHero, setScrolledPastHero] = useState(!isHome);

  useEffect(() => {
    api
      .get("/categories")
      .then(({ data }) => setCategories(data.map((c) => c.name)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isHome) {
      setScrolledPastHero(true);
      return;
    }
    const onScroll = () => setScrolledPastHero(window.scrollY > 360);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (where.trim()) params.set("city", where.trim());
    if (date) {
      params.set(
        "date",
        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
          date.getDate()
        ).padStart(2, "0")}`
      );
    }
    setMobileSearchOpen(false);
    navigate(`/search?${params.toString()}`);
  };

  const onPickSuggestion = (item) => {
    setSuggestOpen(false);
    const params = new URLSearchParams();
    if (item.type === "category") {
      params.set("category", item.value);
    } else {
      setQ(item.value);
      params.set("q", item.value);
    }
    if (where.trim()) params.set("city", where.trim());
    navigate(`/search?${params.toString()}`);
  };

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSearchOpen(false);
  }, [location.pathname]);

  const overlay = isHome && !scrolledPastHero;

  return (
    <header
      className={`z-40 w-full transition-colors duration-300 ${
        overlay
          ? "absolute inset-x-0 top-0 border-b border-transparent bg-transparent"
          : "sticky top-0 border-b border-border bg-white/95 backdrop-blur"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-3 sm:gap-4 sm:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <img src="/logo.png" alt="SHUWA" className="h-14 w-auto object-contain sm:h-20" />
        </Link>

        <form
          onSubmit={onSearch}
          className={`relative hidden flex-1 items-stretch rounded-full border border-border transition-all duration-300 md:flex ${
            scrolledPastHero
              ? "max-w-full opacity-100"
              : "pointer-events-none max-w-0 border-transparent opacity-0"
          }`}
        >
          <div className="relative flex flex-1 items-center gap-2 px-4">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setSuggestOpen(true)}
              placeholder="Search services or businesses"
              className="w-full py-2.5 text-sm outline-none"
            />
            <SearchSuggestions
              query={q}
              open={suggestOpen}
              onClose={() => setSuggestOpen(false)}
              onPick={onPickSuggestion}
              categories={categories}
            />
          </div>
          <div className="w-px bg-border" />
          <div className="relative flex flex-1 items-center gap-2 px-4">
            <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              value={where}
              onChange={(e) => setWhere(e.target.value)}
              onFocus={() => setWhereSuggestOpen(true)}
              placeholder="Where?"
              className="w-full py-2.5 text-sm outline-none"
            />
            <LocationSuggestions
              query={where}
              open={whereSuggestOpen}
              onClose={() => setWhereSuggestOpen(false)}
              onPick={(city) => {
                setWhere(city);
                setWhereSuggestOpen(false);
              }}
            />
          </div>
          <div className="w-px bg-border" />
          <div className="relative flex items-center gap-2 px-4">
            <button
              type="button"
              onClick={() => setDateOpen((v) => !v)}
              className={`flex items-center gap-2 text-sm ${
                date ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              <Calendar className="h-4 w-4 shrink-0" />
              {date
                ? date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
                : "When?"}
            </button>
            <DatePickerPopover
              open={dateOpen}
              onClose={() => setDateOpen(false)}
              value={date}
              onChange={setDate}
            />
          </div>
          <button type="submit" className="rounded-r-full bg-primary px-5 text-sm font-semibold text-white">
            Search
          </button>
        </form>

        <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => setMobileSearchOpen((v) => !v)}
            aria-label="Search"
            className={`flex h-9 w-9 items-center justify-center rounded-full md:hidden ${
              overlay ? "text-white" : "text-foreground"
            }`}
          >
            <Search className="h-5 w-5" />
          </button>

          <Link
            to="/for-business"
            className={`hidden text-sm font-medium sm:inline ${
              overlay
                ? "text-white/90 hover:text-white"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Have a business?
          </Link>
          {user && <NotificationBell user={user} className={overlay ? "text-white" : "text-foreground"} />}
          {user ? (
            <Link
              to={
                user.role === "admin"
                  ? "/admin"
                  : user.role === "business_owner"
                  ? "/business"
                  : "/account"
              }
              className={`hidden items-center gap-2 rounded-full border px-3 py-2 sm:flex ${
                overlay ? "border-white/40 text-white" : "border-border"
              }`}
            >
              <User className="h-4 w-4" />
              <span className="text-sm font-medium">{user.full_name}</span>
            </Link>
          ) : (
            <div className="hidden items-center gap-2 sm:flex">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => openAuthModal("login")}
                className={overlay ? "text-white hover:bg-white/10" : ""}
              >
                Log in
              </Button>
              <Button
                type="button"
                variant={overlay ? "primary" : "dark"}
                size="sm"
                onClick={() => openAuthModal("register")}
              >
                Sign up
              </Button>
            </div>
          )}

          <button
            type="button"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Menu"
            className={`flex h-9 w-9 items-center justify-center rounded-full sm:hidden ${
              overlay ? "text-white" : "text-foreground"
            }`}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileSearchOpen && (
        <form
          onSubmit={onSearch}
          className="border-t border-border bg-white px-4 py-3 md:hidden"
        >
          <div className="flex items-center gap-2 rounded-full border border-border px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search services or businesses"
              className="w-full text-sm outline-none"
            />
          </div>
          <div className="relative mt-2 flex items-center gap-2 rounded-full border border-border px-3 py-2">
            <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              value={where}
              onChange={(e) => setWhere(e.target.value)}
              onFocus={() => setMobileWhereSuggestOpen(true)}
              placeholder="Where?"
              className="w-full text-sm outline-none"
            />
            <LocationSuggestions
              query={where}
              open={mobileWhereSuggestOpen}
              onClose={() => setMobileWhereSuggestOpen(false)}
              onPick={(city) => {
                setWhere(city);
                setMobileWhereSuggestOpen(false);
              }}
            />
          </div>
          <button
            type="submit"
            className="mt-2 w-full rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white"
          >
            Search
          </button>
        </form>
      )}

      {mobileMenuOpen && (
        <div className="border-t border-border bg-white px-4 py-3 sm:hidden">
          <div className="flex flex-col gap-1">
            {user ? (
              <Link
                to={
                  user.role === "admin"
                    ? "/admin"
                    : user.role === "business_owner"
                    ? "/business"
                    : "/account"
                }
                className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium text-foreground hover:bg-muted"
              >
                <User className="h-4 w-4" /> {user.full_name}
              </Link>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    openAuthModal("login");
                  }}
                  className="rounded-xl px-3 py-2.5 text-left text-sm font-medium text-foreground hover:bg-muted"
                >
                  Log in
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    openAuthModal("register");
                  }}
                  className="rounded-xl px-3 py-2.5 text-left text-sm font-medium text-foreground hover:bg-muted"
                >
                  Sign up
                </button>
              </>
            )}
            <Link
              to="/for-business"
              className="rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              Have a business?
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
